export interface CheckItem {
  id: string;
  text: string;
}

export interface CheckGroup {
  id: string;
  title: string;
  items: CheckItem[];
}

/** A beginner's checklist. It is a prompt, not legal advice. */
export const CHECKLIST: readonly CheckGroup[] = [
  {
    id: 'before',
    title: 'Before you pay',
    items: [
      { id: 'history', text: 'Ran a vehicle history check: no outstanding finance, not stolen, not written off.' },
      { id: 'v5c', text: 'The V5C logbook is present, the seller is the registered keeper and the address matches.' },
      { id: 'vin', text: 'The VIN on the car matches the logbook and the history check.' },
      { id: 'mot', text: 'Checked the MOT history online: mileage rises sensibly and the failures make sense.' },
      { id: 'drive', text: 'Test drove it, and looked under the bonnet and at the underside for leaks and rust.' },
      { id: 'panels', text: 'Checked panel gaps, paint match and tyres for accident repair or uneven wear.' },
      { id: 'maxprice', text: 'The price is at or below the maximum offer the calculator gave for my target profit.' },
    ],
  },
  {
    id: 'money',
    title: 'Money, insurance and rules',
    items: [
      { id: 'insured', text: 'The car is insured for what I am doing before it is driven away (check motor trade cover).' },
      { id: 'taxed', text: 'The car is taxed, or kept off the road with a SORN. Untaxed cars cannot be driven on the road.' },
      { id: 'trader', text: 'I understand that buying to sell regularly counts as trading, with tax and consumer-law duties.' },
      { id: 'receipts', text: 'I keep receipts for the car, parts and costs so my profit figure is real.' },
      { id: 'cap', text: 'I am not putting in more cash than I can afford to lose on one car.' },
    ],
  },
  {
    id: 'selling',
    title: 'Selling',
    items: [
      { id: 'honest', text: 'The advert describes the car honestly, faults included. "Sold as seen" does not excuse a misleading description.' },
      { id: 'photos', text: 'Photos are recent, show the whole car and show the faults.' },
      { id: 'payment', text: 'Payment is cleared funds in my bank before the keys and logbook change hands. No cheques; beware overpayment scams.' },
      { id: 'receipt', text: 'A written receipt records the price, date, mileage, both names and the condition.' },
      { id: 'dvla', text: 'I tell DVLA straight away that I have sold the car (the new keeper section of the V5C).' },
    ],
  },
];

export const ALL_CHECK_IDS: readonly string[] = CHECKLIST.flatMap((g) => g.items.map((i) => i.id));

/** Share of checklist items ticked, 0 to 1. */
export function checkProgress(checks: Record<string, boolean>): number {
  const done = ALL_CHECK_IDS.filter((id) => checks[id]).length;
  return ALL_CHECK_IDS.length === 0 ? 0 : done / ALL_CHECK_IDS.length;
}
